
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Mail, Phone, Building2, Eye, UserPlus, DollarSign, X, CheckSquare } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useSellerNotifications } from "@/hooks/useSellerNotifications";
import { generateInitials } from "@/utils/userUtils";
import { AddToListDialog } from "@/components/seller/dialogs/AddToListDialog";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TourWrapper from "@/components/onboarding/TourWrapper";
import { sellerNotificationsSteps } from "@/config/tours/sellerNotificationsTour";
import { safeStorage } from "@/utils/safariStorage";

const NotificationsPage = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, isLoading } = useSellerNotifications();
  const [selectedBuyer, setSelectedBuyer] = useState<{ id: string; name: string } | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Tour management
  useEffect(() => {
    const tourId = 'seller_notifications';
    const hasSeenTour = safeStorage.getItem(`godyspo_tour_${tourId}`) === 'true';
    const shouldStartTour = location.state?.startTour === tourId;
    
    if ((!hasSeenTour || shouldStartTour) && notifications.length > 0) {
      const checkAndStartTour = (retries = 0) => {
        const headerElement = document.querySelector('[data-tour="notifications-header"]');
        if (headerElement) {
          setTimeout(() => setRunTour(true), 500);
        } else if (retries < 10) {
          setTimeout(() => checkAndStartTour(retries + 1), 200);
        }
      };
      checkAndStartTour();
    }
  }, [location.state, notifications.length]);

  const handleTourComplete = () => {
    safeStorage.setItem('godyspo_tour_seller_notifications', 'true');
    setRunTour(false);
  };

  const handleTourSkip = () => {
    safeStorage.setItem('godyspo_tour_seller_notifications', 'true');
    setRunTour(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleNotificationClick = (notificationId: string) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (notification && !notification.read_status) {
      markAsRead(notificationId);
    }
  };

  const handleAddToList = (event: React.MouseEvent, buyerId: string, buyerName: string) => {
    event.stopPropagation();
    setSelectedBuyer({ id: buyerId, name: buyerName });
    setIsDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Notifications</h1>
        </div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <TourWrapper
        steps={sellerNotificationsSteps}
        run={runTour}
        onComplete={handleTourComplete}
        onSkip={handleTourSkip}
      />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between" data-tour="notifications-header">
          <div>
            <h1 className="text-2xl font-bold">Notifications</h1>
            <p className="text-muted-foreground">
              Track buyer interest in your property listings
            </p>
          </div>
          
          {unreadCount > 0 && (
            <div className="flex items-center gap-3">
              <Badge variant="secondary">
                {unreadCount} unread
              </Badge>
              <Button onClick={markAllAsRead} variant="outline" size="sm">
                Mark all as read
              </Button>
            </div>
          )}
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <Card>
            <CardContent className="p-12">
              <div className="text-center">
                <Eye className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No notifications yet</h3>
                <p className="text-muted-foreground">
                  You'll receive notifications when buyers view your property listings.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {notifications.map((notification, index) => (
              <Card 
                key={notification.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  !notification.read_status ? 'ring-2 ring-blue-200 bg-blue-50/30' : ''
                }`}
                onClick={() => handleNotificationClick(notification.id)}
                {...(index === 0 ? { 'data-tour': 'notification-card' } : {})}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Avatar / Icon */}
                    {notification.type === 'task_reminder' ? (
                      <div className="h-12 w-12 flex-shrink-0 rounded-full bg-amber-100 flex items-center justify-center">
                        <CheckSquare className="h-6 w-6 text-amber-600" />
                      </div>
                    ) : (
                      <Avatar className="h-12 w-12 flex-shrink-0">
                        {notification.buyer?.profile_image_url && (
                          <AvatarImage 
                            src={notification.buyer.profile_image_url} 
                            alt={notification.buyer?.full_name || ''} 
                          />
                        )}
                        <AvatarFallback>
                          {generateInitials(notification.buyer?.full_name)}
                        </AvatarFallback>
                      </Avatar>
                    )}

                    {/* Notification Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-lg">
                              {notification.type === 'task_reminder' 
                                ? notification.metadata?.task_title || 'Task Reminder'
                                : notification.buyer?.full_name}
                            </h3>
                            {!notification.read_status && (
                              <div className="w-2 h-2 bg-primary rounded-full" />
                            )}
                          </div>
                          <p className="text-muted-foreground flex items-center gap-2">
                            {notification.type === 'task_reminder' ? (
                              <>
                                <CheckSquare className="h-4 w-4" />
                                {notification.metadata?.is_overdue ? 'Task overdue' : 'Task due today'}
                              </>
                            ) : notification.type === 'property_offer' ? (
                              <>
                                <DollarSign className="h-4 w-4" />
                                Submitted an offer on your property
                              </>
                            ) : notification.type === 'offer_withdrawn' ? (
                              <>
                                <X className="h-4 w-4" />
                                Withdrew their offer on your property
                              </>
                            ) : (
                              <>
                                <Eye className="h-4 w-4" />
                                Viewed your property listing
                              </>
                            )}
                          </p>
                        </div>
                        
                        <div className="text-right text-sm text-muted-foreground">
                          <p>{formatDateTime(notification.created_at)}</p>
                          <p>{formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}</p>
                        </div>
                      </div>

                      {/* Task Info or Property Info */}
                      {notification.type === 'task_reminder' ? (
                        <div className="bg-muted/50 rounded-lg p-4 mb-4">
                          <div className="flex-1">
                            <h4 className="font-medium mb-1">
                              Deal: {notification.metadata?.deal_name || 'Unknown'}
                            </h4>
                            <p className="text-sm text-muted-foreground mb-1">
                              Due: {notification.metadata?.due_date}
                            </p>
                            <p className={`text-sm font-semibold ${notification.metadata?.is_overdue ? 'text-destructive' : 'text-[#ffd700]'}`}>
                              {notification.metadata?.is_overdue ? 'Overdue' : 'Due Today'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="bg-muted/50 rounded-lg p-4 mb-4"
                          {...(index === 0 ? { 'data-tour': 'property-context' } : {})}
                        >
                          <div className="flex items-start gap-4">
                            {notification.property?.image_url && (
                              <img 
                                src={notification.property.image_url} 
                                alt="Property"
                                className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                              />
                            )}
                            <div className="flex-1">
                              <h4 className="font-medium mb-1">
                                {notification.property?.address}
                              </h4>
                              <p className="text-sm text-muted-foreground mb-1">
                                {notification.property?.city}, {notification.property?.state}
                              </p>
                              <p className="text-sm font-semibold text-[#ffd700]">
                                {notification.property?.price && formatCurrency(notification.property.price)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Buyer Contact or Task Actions */}
                      {notification.type === 'task_reminder' ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/seller/deal-tracker');
                            }}
                            className="gap-2"
                          >
                            <CheckSquare className="h-4 w-4" />
                            View Deal Tracker
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="flex items-center gap-2 text-sm">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              <span>{notification.buyer?.email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <span>{notification.buyer?.phone_number}</span>
                            </div>
                            
                            {notification.buyer?.company_name && (
                              <div className="flex items-center gap-2 text-sm">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                <span>{notification.buyer.company_name}</span>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 text-sm">
                              {notification.type === 'property_offer' ? (
                                <>
                                  <DollarSign className="h-4 w-4 text-green-600" />
                                  <span className="font-semibold text-green-600">Offer submitted</span>
                                </>
                              ) : notification.type === 'offer_withdrawn' ? (
                                <>
                                  <X className="h-4 w-4 text-orange-600" />
                                  <span className="font-semibold text-orange-600">Offer withdrawn</span>
                                </>
                              ) : (
                                <>
                                  <Eye className="h-4 w-4 text-muted-foreground" />
                                  <span>Property viewed</span>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => handleAddToList(e, notification.buyer_id!, notification.buyer?.full_name || 'Unknown Buyer')}
                              className="gap-2"
                              {...(index === 0 ? { 'data-tour': 'add-to-list' } : {})}
                            >
                              <UserPlus className="h-4 w-4" />
                              Add to List
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Add to List Dialog */}
        {selectedBuyer && (
          <AddToListDialog
            open={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            buyerId={selectedBuyer.id}
            buyerName={selectedBuyer.name}
          />
        )}
      </div>
    </>
  );
};

export default NotificationsPage;
